// FROM https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/TextField/TextFieldContextMenu/index.tsx

import { Box } from '@modusign/design-system';
import { FontType, TextAlign } from '@modusign/react-signing/types';
import { MouseEvent as ReactMouseEvent, ReactNode, useCallback } from 'react';

import { OnOffButton } from '@app/components/Buttons';
import {
  ContextMenuGroup,
  ContextMenuRow,
  ContextMenuRowDivider,
  ContextMenuWrapper,
} from '@app/components/ContextMenu';
import ContextMenuRowParticipantName from '@app/components/ContextMenuRowParticipantName';
import DataLabelInput from '@app/components/DataLabelInput';
import {
  FONT_SIZES,
  FONT_TYPES_MAP,
  SELECTABLE_FONT_TYPE,
} from '@app/components/TextField/constants';
import ContextMenuSelect from '@app/features/apply-react-signing/components/FieldSetupStep/ContextMenuSelect';
import ContextMenuTextAlignRow from '@app/features/apply-react-signing/components/FieldSetupStep/ContextMenuTextAlignRow';

const FONT_POINT_OPTIONS = FONT_SIZES.map((size) => ({ value: size, displayText: `${size}pt` }));
const FONT_TYPE_OPTIONS = SELECTABLE_FONT_TYPE.map((type) => ({
  value: type,
  displayText: FONT_TYPES_MAP[type],
}));

export interface TextBaseFieldContextMenuProps {
  dataLabel: string;
  dataLabelErrorMessage: string;
  fontFamily: FontType;
  fontSize: number;
  isRequired: boolean;
  participantName?: string;
  participantOrder?: number;
  textAlign: TextAlign;
  isDataLabelEditable?: boolean;
  children?: ReactNode;
  onDataLabelChange: (value: string) => void;
  onFontFamilyChange: (fontFamily: FontType) => void;
  onFontSizeChange: (fontSize: number) => void;
  onRequiredToggle: (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    nextIsRequired: boolean
  ) => void;
  onTextAlignChange: (textAlign: TextAlign) => void;
}

function TextBaseFieldContextMenu({
  dataLabel,
  dataLabelErrorMessage,
  fontFamily,
  fontSize,
  isRequired,
  participantName,
  participantOrder,
  textAlign,
  isDataLabelEditable,
  children,
  onDataLabelChange,
  onFontFamilyChange,
  onFontSizeChange,
  onRequiredToggle,
  onTextAlignChange,
}: TextBaseFieldContextMenuProps) {
  const handleFontFamilyChange = useCallback(
    (fontFamily: FontType) => {
      onFontFamilyChange(fontFamily);
    },
    [onFontFamilyChange]
  );

  const handleFontSizeChange = useCallback(
    (fontSize: number) => {
      onFontSizeChange(fontSize);
    },
    [onFontSizeChange]
  );

  const handleRequiredChange = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      onRequiredToggle(e, !isRequired);
    },
    [isRequired, onRequiredToggle]
  );

  return (
    <ContextMenuWrapper>
      {participantName && participantOrder !== undefined && (
        <>
          <ContextMenuRowParticipantName
            participantName={participantName}
            participantOrder={participantOrder}
          />
          <ContextMenuRowDivider />
        </>
      )}
      <ContextMenuRow>
        <b>필수 입력</b>
        <OnOffButton
          active={isRequired}
          data-testid="text-field-required-btn"
          style={{ float: 'right' }}
          onClick={handleRequiredChange}
        />
      </ContextMenuRow>
      <ContextMenuRowDivider />
      <ContextMenuRow>
        <ContextMenuGroup>
          <ContextMenuSelect
            menuName="글자 크기"
            options={FONT_POINT_OPTIONS}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={FONT_SIZES.indexOf(fontSize)}
            onOptionChange={(option) => handleFontSizeChange(option.value)}
          />
          <ContextMenuSelect
            menuName="글자체"
            options={FONT_TYPE_OPTIONS}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={FONT_TYPE_OPTIONS.findIndex(
              (option) => option.value === fontFamily
            )}
            onOptionChange={(option) => handleFontFamilyChange(option.value)}
          />
          <ContextMenuTextAlignRow selectedTextAlign={textAlign} onChange={onTextAlignChange} />
        </ContextMenuGroup>
      </ContextMenuRow>
      <>{children}</>
      {isDataLabelEditable && (
        <>
          <ContextMenuRowDivider />
          <Box pb="20px" pt="10px" textAlign="left">
            <DataLabelInput
              errorMessage={dataLabelErrorMessage}
              value={dataLabel}
              onChange={onDataLabelChange}
            />
          </Box>
        </>
      )}
    </ContextMenuWrapper>
  );
}

export default TextBaseFieldContextMenu;
